import type { TFunction } from "i18next";
import { TourStep } from "../TourProvider";
import { ROUTES, type TourRouteKey } from "~/utils/routing/routeUtils";
import type { SetupWalkthroughPhase } from "./setupWalkthrough";
import {
  accountTour,
  addBuildingTour,
  addPropertyTour,
  addUnitTour,
  amenityDetailsTour,
  amenityTableTour,
  buildingDetailsSetupCompleteTour,
  buildingDetailsSetupTour,
  buildingDetailsTour,
  buildingTableTour,
  financeTour,
  homePageTour,
  leaseDetailsTour,
  leaseTableTour,
  messagesTour,
  orgMemberDetailsTour,
  organizationDetailsTour,
  paymentsTour,
  propertyDetailsSetupTour,
  propertyDetailsTour,
  propertyTableSetupTour,
  propertyTableTour,
  serviceRequestDetailsTour,
  serviceRequestTableTour,
  tenantDetailsTour,
  tenantTableTour,
  unitDetailsSetupCompleteTour,
  unitDetailsTour,
  unitPlanDetailsTour,
  unitPlanTableTour,
  unitTableTour,
} from "../tourConfigs";

export interface GetTourForRouteOptions {
  /** When true, prefer slim setup-walkthrough tours where defined. */
  setup?: boolean;
  /** Current setup phase — drives property/building/unit vs complete tours. */
  setupPhase?: SetupWalkthroughPhase;
}

/**
 * Maps route keys to their corresponding tour configurations
 * This ensures routes are tracked via the ROUTES constant
 */
const tourRouteMap: Record<TourRouteKey, (t: TFunction) => TourStep[] | null> =
  {
    home: homePageTour,
    propertyTable: propertyTableTour,
    propertyDetails: propertyDetailsTour,
    addProperty: () => null,
    buildingTable: buildingTableTour,
    buildingDetails: buildingDetailsTour,
    addBuilding: () => null,
    unitTable: unitTableTour,
    unitDetails: unitDetailsTour,
    addUnit: () => null,
    tenantTable: tenantTableTour,
    tenantDetails: tenantDetailsTour,
    addTenant: () => null,
    leaseTable: leaseTableTour,
    leaseDetails: leaseDetailsTour,
    serviceRequest: serviceRequestTableTour,
    serviceRequestDetails: serviceRequestDetailsTour,
    organization: () => null,
    organizationDetails: organizationDetailsTour,
    orgMemberDetails: orgMemberDetailsTour,
    calendar: () => null,
    messages: messagesTour,
    settings: () => null,
    account: accountTour,
    help: () => null,
    payments: paymentsTour,
    finance: financeTour,
    amenity: amenityTableTour,
    amenityDetails: amenityDetailsTour,
    unitPlan: unitPlanTableTour,
    unitPlanDetails: unitPlanDetailsTour,
  };

/**
 * Setup-walkthrough overrides for core create chain + completion.
 */
const setupTourRouteMap: Partial<
  Record<
    TourRouteKey,
    (t: TFunction, phase: SetupWalkthroughPhase) => TourStep[] | null
  >
> = {
  propertyTable: (t) => propertyTableSetupTour(t),
  addProperty: (t) => addPropertyTour(t),
  propertyDetails: (t, phase) =>
    phase === "building" || phase === "property"
      ? propertyDetailsSetupTour(t)
      : null,
  addBuilding: (t) => addBuildingTour(t),
  buildingDetails: (t, phase) => {
    if (phase === "complete") {
      return buildingDetailsSetupCompleteTour(t);
    }
    if (phase === "unit" || phase === "building") {
      return buildingDetailsSetupTour(t);
    }
    return null;
  },
  addUnit: (t) => addUnitTour(t),
  unitDetails: (t, phase) =>
    phase === "complete" ? unitDetailsSetupCompleteTour(t) : null,
  // Optional explore destinations reuse normal page tours
  amenity: (t) => amenityTableTour(t),
  serviceRequest: (t) => serviceRequestTableTour(t),
  payments: (t) => paymentsTour(t),
  organizationDetails: (t) => organizationDetailsTour(t),
};

const resolveRouteKey = (pathname: string): TourRouteKey | null => {
  const routeEntries = Object.entries(ROUTES) as [TourRouteKey, string][];

  // Prefer more specific (longer / more segments) patterns first so
  // `/property/:id/amenity/:id` wins over `/property/:id`.
  const sortedEntries = [...routeEntries].sort(
    (a, b) => b[1].split("/").length - a[1].split("/").length
  );

  for (const [routeKey, routePath] of sortedEntries) {
    if (!(routeKey in tourRouteMap)) {
      continue;
    }

    if (routePath === pathname) {
      return routeKey;
    }

    if (routePath.includes(":")) {
      const pathSegments = pathname.split("/").filter(Boolean);
      const patternSegments = routePath.split("/").filter(Boolean);

      if (pathSegments.length !== patternSegments.length) {
        continue;
      }

      let matches = true;
      for (let i = 0; i < patternSegments.length; i++) {
        const patternSegment = patternSegments[i];
        const pathSegment = pathSegments[i];

        if (patternSegment.startsWith(":")) {
          if (!pathSegment) {
            matches = false;
            break;
          }
        } else if (patternSegment !== pathSegment) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return routeKey;
      }
    }
  }

  return null;
};

/**
 * Maps route paths to their corresponding tour configurations
 * Uses ROUTES constant to ensure route tracking
 * @param pathname - Current route pathname
 * @param t - Translation function from i18next
 * @param options - Optional setup walkthrough overrides
 * @returns TourStep[] if tour exists, null otherwise
 */
export const getTourForRoute = (
  pathname: string,
  t: TFunction,
  options?: GetTourForRouteOptions
): TourStep[] | null => {
  const routeKey = resolveRouteKey(pathname);
  if (!routeKey) {
    return null;
  }

  if (options?.setup) {
    const setupFactory = setupTourRouteMap[routeKey];
    if (setupFactory) {
      const phase = options.setupPhase ?? "property";
      const setupSteps = setupFactory(t, phase);
      if (setupSteps) {
        return setupSteps;
      }
    }
    // Fall through to normal tour when setup has no override for this route/phase
  }

  return tourRouteMap[routeKey](t);
};
